import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Dialog, DialogContent } from '@tinct/web-components';
import { useJobStore } from '@tinct/web-layout';
import type { ImportResponse } from '@tinct/core-api-models';
import { ImportingStep, SuccessStep } from './steps';

const HASH_RE = /^#buyer-import=([^&]+)$/;

/**
 * Mounted globally — listens for `#buyer-import=<jobId>` in the URL hash
 * (typically reached by clicking a `pendingUrl` link in the JobAlertWidget)
 * and reopens the import flow on the importing step.
 */
export const ResumeBuyerImportModal = () => {
  const { hash, pathname } = useLocation();
  const navigate = useNavigate();
  const jobs = useJobStore((s) => s.jobs);

  const jobId = useMemo(() => {
    const m = hash.match(HASH_RE);
    return m?.[1] ?? null;
  }, [hash]);

  // Synthetic import result. Real import responses include richer fields
  // (success/error counts) — we only know the jobId from the URL, so the
  // success step shows minimal info.
  const [importResult, setImportResult] = useState<ImportResponse | null>(null);

  if (!jobId) return null;

  const job = jobs.find((j) => j.id === jobId);

  const clearHash = () => {
    navigate(pathname, { replace: true });
    setImportResult(null);
  };

  const handleCompleted = () => {
    setImportResult({
      jobId,
      successCount: null,
      errorCount: null,
      errorDetails: null,
    });
  };

  const handleDone = () => {
    if (job?.completedUrl) {
      navigate(job.completedUrl, { replace: true });
    } else {
      clearHash();
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && clearHash()}>
      <DialogContent className="sm:max-w-[425px] rounded-3xl p-0 border-0 bg-transparent shadow-none">
        {!importResult ? (
          <ImportingStep
            jobId={jobId}
            onJobCompleted={handleCompleted}
            onRunInBackground={clearHash}
          />
        ) : (
          <SuccessStep importResult={importResult} onDone={handleDone} />
        )}
      </DialogContent>
    </Dialog>
  );
};
